import { VirtualCurrencies, VirtualCurrencyCode } from "@/@types/currencies";
import React from "react";
import { PostRequest, useRequest } from "./useRequests";
import { useXSSOAuth } from "./useXSSOAuth";

export type BalanceType = { amount: number; currency: VirtualCurrencyCode };

export const useBalance = (gameId?: string) => {
  const { isLoggedIn, loading, login } = useXSSOAuth();
  const [balanceRequest, setBalanceRequest] = React.useState<PostRequest>({} as PostRequest);

  React.useEffect(() => {
    if (loading || !isLoggedIn) {
      return;
    }
    setBalanceRequest({
      url: `/v3/wallets/get-wallet-balance`,
      method: "POST",
      data: { gameId: gameId },
    });
  }, [isLoggedIn, gameId]);

  const {
    isLoading,
    data: balanceResponse,
    ...rest
  } = useRequest<{ balance: BalanceType[] }>(balanceRequest, {
    // TODO: HermesError handling
    // SWR Doc: https://swr.vercel.app/docs/revalidation
    revalidateOnFocus: true, // SWR: When browser tab focus
    revalidateOnMount: true, // SWR: When the component is mounted we are going to do the request again
    focusThrottleInterval: 60000, // SWR: Interval between revalidations in milliseconds
  });

  // useRequest starts with an empty request, which has no isLoading key, so when we use balance.isLoading, we get false.
  // the reality is that the call hasn't happened yet while the hook is initialising.
  // setting isLoading to true when there is no url, means that we can use balance.isLoading and get true until the call has been made.
  if (!balanceRequest.url) {
    return {
      isLoading: true,
    };
  }

  if (rest.response?.status === 401) {
    login();
    return {
      isLoading,
    };
  }
  const balance: { [P in VirtualCurrencies]: number } = {} as any;
  balanceResponse?.balance?.forEach((bal) => {
    balance[bal.currency] = bal.amount;
  });

  return {
    isLoading,
    balance,
  };
};
